import React from "react";
import { getLanguageValue } from "locales/i18n";
import { PAGE_TYPE } from "enums";

import LoadingComponent from "commons/loading/Loading.component";
import { getImageUrl } from "helpers/Image.helper";
import { Link } from "react-router-dom";
import { getFlagByCountryCode, getStudentNickname } from "helpers/String.helper";
import TimelineContainer from "./Timeline.container";

// 타임라인용 스켈레톤 컴포넌트
const HomeSkeleton = () => (
    <div className="container mx-auto">
        {/* 포스트 작성 폼 스켈레톤 */}
        <div className="bg-white p-4 rounded-2xl shadow-sm mb-4 animate-pulse">
            <div className="space-y-4">
                <div className="flex items-center gap-2">
                    <div className="w-6 h-6 bg-gray-200 rounded"></div>
                    <div className="h-6 bg-gray-200 rounded w-48"></div>
                </div>
                <div className="h-12 bg-gray-200 rounded-xl"></div>
            </div>
        </div>

        {/* 타임라인 스켈레톤 */}
        <div className="space-y-4 px-4">
            {[...Array(3)].map((_, index) => (
                <div key={index} className="bg-white rounded-2xl shadow-sm overflow-hidden animate-pulse">
                    {/* 포스트 헤더 스켈레톤 */}
                    <div className="p-4 sm:p-6 border-b border-gray-100">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                                <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
                                <div className="space-y-2">
                                    <div className="h-4 bg-gray-200 rounded w-24"></div>
                                    <div className="h-3 bg-gray-200 rounded w-20"></div>
                                </div>
                            </div>
                            <div className="w-6 h-6 bg-gray-200 rounded"></div>
                        </div>
                    </div>

                    {/* 포스트 내용 스켈레톤 */}
                    <div className="p-4 sm:p-6">
                        <div className="mb-4 space-y-2">
                            <div className="h-4 bg-gray-200 rounded w-full"></div>
                            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                            <div className="h-4 bg-gray-200 rounded w-4/6"></div>
                        </div>

                        {/* 이미지 스켈레톤 */}
                        <div className="mb-4">
                            <div className="flex gap-2">
                                <div className="w-20 h-20 bg-gray-200 rounded-lg"></div>
                                <div className="w-20 h-20 bg-gray-200 rounded-lg"></div>
                                <div className="w-20 h-20 bg-gray-200 rounded-lg"></div>
                            </div>
                        </div>

                        {/* 메타 정보 스켈레톤 */}
                        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                            <div className="flex items-center space-x-6">
                                <div className="flex items-center space-x-2">
                                    <div className="w-5 h-5 bg-gray-200 rounded"></div>
                                    <div className="h-4 bg-gray-200 rounded w-8"></div>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <div className="w-5 h-5 bg-gray-200 rounded"></div>
                                    <div className="h-4 bg-gray-200 rounded w-8"></div>
                                </div>
                            </div>
                            <div className="h-4 bg-gray-200 rounded w-16"></div>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    </div>
);

const HomeComponent = (props: any) => {
    const { info,
        isLoading,
        isOnSendEmailForParentConfirmation,
        isOnSendEmailForEmailVerification,
        handleSendEmailForParentConfirmation,
        handleSendEmailForEmailVerification,
        requestChatList,
        posts,
        isLoadingPosts,
        isPosting,
        hasMore,
        fileInputRef,
        onImageUpload,
        onRemoveImage,
        onContentChange,
        onPost,
        onLoadMore,
        images,
        content,
        setPosts,
        onRequestClick,

        handlePostWritingPopup,

        handleShowStudentInfoPopup,
        handleShowCongratulationInfoPopup,
        handleShowFullPageFollowPopup,
        subject,
        onSubjectChange,
    } = props;

    return (
        <>
            <main className="bg-gray-50 min-h-screen ">
                <>
                    {isLoading ? (
                        <HomeSkeleton />
                    ) : (
                        <div className="container mx-auto">
                            {info &&
                                <>
                                    {/* 부모 이메일 인증 */}
                                    {info.under14 && !info.isParentConfirm && (
                                        <div className="bg-white rounded-2xl shadow-sm overflow-hidden mb-2 sm:mb-6">
                                            <div className="p-4 sm:p-6 border-b border-gray-100">
                                                <h2 className="text-base sm:text-lg md:text-xl font-bold text-gray-900">
                                                    {getLanguageValue(PAGE_TYPE.STUDENT, 'home.txtNeedParentConfirm')}
                                                </h2>
                                            </div>
                                            <div className="p-4 sm:p-6">
                                                <div className="space-y-3 sm:space-y-4">
                                                    <p className="text-sm sm:text-base text-gray-600">
                                                        <strong className="text-[#CF3688] break-all">{info.parentEmail}</strong> {getLanguageValue(PAGE_TYPE.STUDENT, 'home.txtNeedParentConfirmDescription')}
                                                    </p>
                                                    <p className="text-sm sm:text-base text-gray-600">
                                                        {getLanguageValue(PAGE_TYPE.STUDENT, 'home.txtWarningEmailDesc')}
                                                    </p>
                                                    <button className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-2.5 text-sm sm:text-base bg-[#CF3688] text-white font-medium rounded-lg 
                                                hover:bg-[#a62d6d] focus:ring-4 focus:ring-pink-300 transition duration-200
                                                disabled:bg-gray-300 disabled:cursor-not-allowed disabled:hover:bg-gray-300"
                                                        onClick={handleSendEmailForParentConfirmation}
                                                        disabled={isOnSendEmailForParentConfirmation}>
                                                        {getLanguageValue(PAGE_TYPE.STUDENT, 'home.btnSendEmailForParentConfirmation')}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* 가입 이메일 인증 */}
                                    {!info.hasVerifiedEmail && (
                                        <div className="bg-white rounded-2xl shadow-sm overflow-hidden mb-4 sm:mb-6">
                                            <div className="p-4 sm:p-6 border-b border-gray-100">
                                                <h2 className="text-base sm:text-lg md:text-xl font-bold text-gray-900">
                                                    {getLanguageValue(PAGE_TYPE.STUDENT, 'home.txtNeedVerifyEmail')}
                                                </h2>
                                            </div>
                                            <div className="p-4 sm:p-6">
                                                <div className="space-y-3 sm:space-y-4">
                                                    <p className="text-sm sm:text-base text-gray-600">
                                                        {getLanguageValue(PAGE_TYPE.STUDENT, 'home.txtNeedVerifyEmailDescription')}
                                                    </p>
                                                    <p className="text-sm sm:text-base text-gray-600">
                                                        {getLanguageValue(PAGE_TYPE.STUDENT, 'home.txtWarningEmailDesc')}
                                                    </p>
                                                    <button className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-2.5 text-sm sm:text-base bg-[#CF3688] text-white font-medium rounded-lg
                                                hover:bg-[#a62d6d] focus:ring-4 focus:ring-pink-300 transition duration-200
                                                disabled:bg-gray-300 disabled:cursor-not-allowed disabled:hover:bg-gray-300"
                                                        onClick={handleSendEmailForEmailVerification}
                                                        disabled={isOnSendEmailForEmailVerification}>
                                                        {getLanguageValue(PAGE_TYPE.STUDENT, 'home.btnSendEmailForEmailVerification')}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* 캐스팅 지망생 목록 섹션 */}
                                    {/* <section className="mb-2">
                                        <div className="p-4 sm:p-6">
                                            <h2 className="text-lg sm:text-xl font-bold text-gray-900">
                                                {getLanguageValue(PAGE_TYPE.STUDENT, "home.txtTraineeListTitle")}
                                            </h2>
                                            <div className="p-4 sm:p-6">
                                                <div className="space-y-4 sm:space-y-6">
                                                    {info.traineeList?.length > 0 ? (
                                                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
                                                            {info.traineeList.map((trainee: any) => (
                                                                <div key={trainee.id} className="p-3 sm:p-4 rounded-lg sm:rounded-xl 
                                                                transition-all duration-200 
                                                                transform hover:-translate-y-1">
                                                                    <div className="flex flex-col h-full items-center">
                                                                        <div className="flex items-center mb-2 justify-center">
                                                                            <div className="w-14 h-14 rounded-full overflow-hidden mr-2 relative">
                                                                                <img
                                                                                    src={getProfileImageUrl(trainee.profileImageInfo)}
                                                                                    alt={trainee.name}
                                                                                    className="w-full h-full object-cover"
                                                                                />
                                                                                <div className="absolute bottom-0 left-0 right-0 text-center">
                                                                                    {getFlagByCountryCode(trainee.countryInfo?.code)}
                                                                                </div>
                                                                            </div>
                                                                            <h3 className="text-sm font-medium text-gray-900">
                                                                                {trainee.name}
                                                                            </h3>
                                                                        </div>
                                                                        <div className="text-xs text-gray-600 mb-2 text-center">
                                                                            <p className="mb-1">{trainee.birthyear} {getGenderEmoji(trainee.genderInfo?.code)}</p>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <p className="text-sm text-gray-500">
                                                            {getLanguageValue(PAGE_TYPE.STUDENT, "home.txtNoCastingWishList")}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </section> */}

                                    {/* 대화요청 목록 */}
                                    {info?.requestChatList?.length > 0 && (
                                        <div className="mb-4">
                                            <div className="p-2 sm:p-3">
                                                <h2 className="text-lg sm:text-xl font-bold text-gray-900">
                                                    {getLanguageValue(PAGE_TYPE.STUDENT, "home.txtRequestChatListTitle")}
                                                    ({info?.requestChatList?.length})
                                                </h2>
                                            </div>
                                            <div className="overflow-x-auto">
                                                <div className="flex gap-1">
                                                    {info?.requestChatList?.map((item: any) => (
                                                        <div
                                                            key={item.id}
                                                            onClick={() => onRequestClick(item)}
                                                            className="w-[100px] sm:w-[125px] lg:w-[150px] flex-shrink-0 cursor-pointer">
                                                            <div className="flex flex-col items-center">
                                                                <div className="rounded-sm overflow-hidden w-14 h-14 sm:w-16 sm:h-16 flex-shrink-0 border-2 border-gray-100">
                                                                    <img
                                                                        src={getImageUrl(item?.userAgencyInfo?.agencyInfo?.logoImageInfo)}
                                                                        alt={item?.userAgencyInfo?.agencyInfo?.nameEn}
                                                                        className="w-full h-full object-contain"
                                                                    />
                                                                </div>
                                                                <div className="mt-2 text-center w-full">
                                                                    <p className="text-gray-900 text-xs truncate px-2">
                                                                        {item?.userAgencyInfo?.agencyInfo?.nameEn}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* 캐스팅 채팅 요청받은 지망생 목록 */}
                                    {info?.congratulationList?.length > 0 && (
                                        <div className="mb-8">
                                            <div className="p-2 sm:p-3 cursor-pointer flex items-center">
                                                <h2 className="text-lg sm:text-xl font-bold text-gray-900">
                                                    {getLanguageValue(PAGE_TYPE.STUDENT, "home.txtCongratulationTitle")}
                                                </h2>
                                            </div>
                                            <div className="overflow-x-auto">
                                                <div className="flex gap-0.25">
                                                    {info?.congratulationList?.map((item: any) => (
                                                        <div key={item.userStudentInfo.id} className="w-[95px] sm:w-[120px] lg:w-[145px] flex-shrink-0 cursor-pointer"
                                                            onClick={() => handleShowCongratulationInfoPopup(item?.id)}
                                                        >
                                                            <div className="flex flex-col items-center">
                                                                <div className="rounded-full overflow-hidden w-14 h-14 sm:w-16 sm:h-16 flex-shrink-0 border-2 border-gray-100 relative">
                                                                    <img
                                                                        src={getImageUrl(item?.userStudentInfo?.profileImageInfo)}
                                                                        alt={getStudentNickname(item?.userStudentInfo)}
                                                                        className="w-full h-full object-cover"
                                                                    />
                                                                    <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2">
                                                                        {getFlagByCountryCode(item?.userStudentInfo?.countryInfo?.code)}
                                                                    </div>
                                                                </div>
                                                                <div className="mt-1 text-center w-full">
                                                                    <p className="text-gray-900 text-xs truncate px-1">
                                                                        {getStudentNickname(item?.userStudentInfo)}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>

                                    )}

                                    {/* 추천 팔로잉 목록 */}
                                    {/* {info?.recommendFollowingList?.length > 0 && (
                                        <div className="mb-8">
                                            <Link to="/student/follow" className="p-2 sm:p-3 cursor-pointer flex items-center">
                                                <h2 className="text-lg sm:text-xl font-bold text-gray-900">
                                                    {getLanguageValue(PAGE_TYPE.STUDENT, "home.txtRecommendedFollowingListTitle")}
                                                </h2>
                                                <span className="text-gray-500">
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" className="inline-block ml-1">
                                                        <path d="M9 18l6-6-6-6" />
                                                    </svg>
                                                </span>
                                            </Link>
                                            <div className="overflow-x-auto">
                                                <div className="flex gap-0.25">
                                                    {info?.recommendFollowingList?.map((studentInfo: any) => (
                                                        <div key={studentInfo.id} className="w-[95px] sm:w-[120px] lg:w-[145px] flex-shrink-0 cursor-pointer"
                                                            onClick={() => handleShowStudentInfoPopup(studentInfo?.id)}
                                                        >
                                                            <div className="flex flex-col items-center">
                                                                <div className="rounded-full overflow-hidden w-14 h-14 sm:w-16 sm:h-16 flex-shrink-0 border-2 border-gray-100 relative">
                                                                    <img
                                                                        src={getImageUrl(studentInfo?.profileImageInfo)}
                                                                        alt={getStudentNickname(studentInfo)}
                                                                        className="w-full h-full object-cover"
                                                                    />
                                                                    <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2">
                                                                        {getFlagByCountryCode(studentInfo?.countryInfo?.code)}
                                                                    </div>
                                                                </div>
                                                                <div className="mt-1 text-center w-full">
                                                                    <p className="text-gray-900 text-xs truncate px-1">
                                                                        {getStudentNickname(studentInfo)}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    )} */}

                                    {/* 팔로잉 목록 */}
                                    {
                                        info?.congratulationList?.length == 0 &&
                                        info?.followingList?.length > 0 && (
                                            <div className="mb-8">
                                                <Link to="/student/follow" className="p-2 sm:p-3 cursor-pointer flex items-center">
                                                    <h2 className="text-lg sm:text-xl font-bold text-gray-900">
                                                        {getLanguageValue(PAGE_TYPE.STUDENT, "home.txtFollowingListTitle")}
                                                    </h2>
                                                    <span className="text-gray-500">
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" className="inline-block ml-1">
                                                            <path d="M9 18l6-6-6-6" />
                                                        </svg>
                                                    </span>
                                                </Link>
                                                <div className="overflow-x-auto">
                                                    <div className="flex gap-1">
                                                        {info?.followingList?.map((item: any) => (
                                                            <div key={item.id} className="w-[100px] sm:w-[125px] lg:w-[150px] flex-shrink-0 cursor-pointer"
                                                                onClick={() => handleShowStudentInfoPopup(item?.followingUserInfo?.userStudentInfo?.id)}
                                                            >
                                                                <div className="flex flex-col items-center">
                                                                    <div className="rounded-full overflow-hidden w-14 h-14 sm:w-16 sm:h-16 flex-shrink-0 border-2 border-gray-100">
                                                                        <img
                                                                            src={getImageUrl(item?.followingUserInfo?.userStudentInfo?.profileImageInfo)}
                                                                            alt={item?.followingUserInfo?.userStudentInfo?.name}
                                                                            className="w-full h-full object-cover"
                                                                        />
                                                                    </div>
                                                                    <div className="mt-2 text-center w-full">
                                                                        <p className="text-gray-900 text-xs truncate px-2">
                                                                            {item?.followingUserInfo?.userStudentInfo?.name}
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                    {/* 팬페이지 */}
                                    {/* <div className="bg-white rounded-2xl shadow-sm overflow-hidden mb-4 sm:mb-6">
                                <div className="p-4 sm:p-6 border-b border-gray-100">
                                    <h2 className="text-lg sm:text-xl font-bold text-gray-900">내 팬페이지</h2>
                                </div>
                                <div className="p-4 sm:p-6">
                                    <a
                                        href={getFanPageUrlBySlug(info.slug)}
                                        className="text-[#CF3688] hover:text-[#a62d6d] transition-colors duration-200 break-all underline"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {getFanPageUrlBySlug(info.slug)}
                                    </a>
                                </div>
                            </div> */}
                                </>
                            }

                            <TimelineContainer
                                info={info}
                            />
                        </div>)
                    }
                </>
            </main >
        </>
    );
};

export default HomeComponent;
